import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { BreadcrumbJsonLd } from '@/components/seo/breadcrumb-json-ld'
import { NewsArticleJsonLd } from '@/components/seo/news-article-json-ld'
import { PageMeta } from '@/components/seo/page-meta'
import { NEWS_CATEGORY_LABELS, getNewsArticleBySlug, getNewsPath } from '@/content/news/articles'
import { PELGO_NOT_FOUND_SEO, getPelgoNewsArticleSeo } from '@/lib/pelgo-seo'
import { NotFoundPage } from '@/pages/not-found'
import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

export function NewsArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? getNewsArticleBySlug(slug) : undefined

  useEffect(() => {
    void slug
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [slug])

  if (!article || !slug) {
    return <NotFoundPage />
  }

  const path = getNewsPath(article.slug)
  const seoConfig = getPelgoNewsArticleSeo(article) ?? PELGO_NOT_FOUND_SEO
  const bodyParagraphs = article.bodyParagraphs ?? []
  const isVideo = article.tags?.includes('video') ?? false
  const publicationLine = article.publicationSeries
    ? `${article.publicationName} — ${article.publicationSeries}`
    : article.publicationName

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={seoConfig} />
      <NewsArticleJsonLd article={article} canonicalPath={seoConfig.canonicalPath ?? path} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', path: '/' },
          { name: 'News', path: '/news' },
          { name: article.title, path },
        ]}
      />
      <MarketingNav variant="standalone" />

      <article className="flex-1 pt-32">
        <div
          className="py-16 md:py-24"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
          }}
        >
          <div className="container mx-auto px-4 md:px-16">
            <div className="max-w-[1280px] mx-auto">
              <p className="mb-6 text-sm text-gray-500">
                <Link to="/news" className="text-pelgo-blue-light font-medium hover:underline">
                  News
                </Link>
                <span className="mx-2 text-gray-300" aria-hidden>
                  /
                </span>
                <span className="text-gray-600">{NEWS_CATEGORY_LABELS[article.category]}</span>
              </p>

              <header className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wide text-pelgo-blue-light mb-4">
                  {article.publicationName}
                </p>
                <div className="h-3 w-12 bg-pelgo-blue-light rounded-full mb-6" aria-hidden />
                <h1 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                  {article.title}
                </h1>
                <p className="mt-6 text-base font-medium text-gray-500 tracking-[-0.32px] leading-relaxed">
                  {article.summary}
                </p>
                <p className="mt-6 text-sm text-gray-600">
                  <span className="font-medium text-gray-900">{article.author}</span>
                  <span className="mx-2 text-gray-400" aria-hidden>
                    |
                  </span>
                  <time dateTime={article.publishedAt}>{article.displayDate}</time>
                </p>
              </header>

              <figure className="mt-10 md:mt-12 max-w-4xl">
                <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)] aspect-[3/2] bg-gray-100">
                  <img
                    src={article.heroImageSrc}
                    alt={article.heroImageAlt}
                    className="h-full w-full object-cover"
                    width={1200}
                    height={800}
                    loading="eager"
                    decoding="async"
                  />
                </div>
                {article.category === 'press' ? (
                  <figcaption className="mt-3 text-xs text-gray-500">
                    {isVideo
                      ? `Still from the original ${article.publicationName} video.`
                      : `Photo accompanying the original ${article.publicationName} piece.`}
                  </figcaption>
                ) : null}
              </figure>

              <div className="mt-10 md:mt-12 max-w-3xl">
                {article.introCallout ? (
                  <p className="text-base text-gray-600 leading-relaxed border-l-2 border-gray-200 pl-5 italic">
                    {article.introCallout}
                  </p>
                ) : null}

                {article.sectionHeading ? (
                  <h2 className="font-display text-2xl md:text-3xl text-gray-900 mt-10 md:mt-14 mb-6 tracking-tight">
                    {article.sectionHeading}
                  </h2>
                ) : null}

                {bodyParagraphs.length > 0 ? (
                  <div className="prose prose-slate max-w-none prose-p:text-gray-700 prose-p:leading-relaxed prose-p:tracking-[-0.01em]">
                    {bodyParagraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 48)} className="text-slate-700 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}

                {article.sourceUrl ? (
                  <aside
                    className="mt-12 md:mt-16 rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8"
                    aria-label="Source"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                      {article.outletLogoSrc ? (
                        <img
                          src={article.outletLogoSrc}
                          alt=""
                          width={140}
                          height={32}
                          className="h-8 w-auto object-contain object-left flex-shrink-0"
                        />
                      ) : null}
                      <div className="min-w-0 flex-1 space-y-3 text-sm text-gray-700 leading-relaxed">
                        <p className="font-medium text-gray-900">{publicationLine}</p>
                        {article.sourcePublishedLabel ? (
                          <p>
                            {isVideo
                              ? 'Interview aired '
                              : article.category === 'press'
                                ? 'Excerpt from coverage dated '
                                : 'Published '}
                            {article.sourcePublishedLabel}
                            {article.sourceNote ? `. ${article.sourceNote}` : '.'}
                          </p>
                        ) : article.sourceNote ? (
                          <p>{article.sourceNote}</p>
                        ) : null}
                        <p>
                          <a
                            href={article.sourceUrl}
                            className="text-pelgo-blue-light font-medium underline-offset-2 hover:underline break-words"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {isVideo
                              ? `Watch on ${article.publicationName}`
                              : `Read the original on ${article.publicationName}`}
                          </a>
                        </p>
                      </div>
                    </div>
                  </aside>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </article>

      <SiteFooter />
    </div>
  )
}
